import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Plus, Pencil, Trash2, Users, Search } from "lucide-react";
import DataPagination from "@/components/DataPagination";
import { HeadCell } from "@/components/SortLabel";
import { useTableSort } from "@/lib/useTableSort";
import ConfirmDelete from "@/components/ConfirmDelete";
import DataTable from "@/components/DataTable";
import { toast } from "sonner";

const empty = { nome: "", email: "", telefono: "", indirizzo: "", note: "" };
const PAGE_SIZE = 8;

export default function Clienti() {
  const [clienti, setClienti] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editing, setEditing] = useState(null);
  const [query, setQuery] = useState("");
  const [page, setPage] = useState(1);

  const load = () => api.get("/clienti").then((r) => setClienti(r.data));
  useEffect(() => { load(); }, []);

  const openNew = () => { setForm(empty); setEditing(null); setOpen(true); };
  const openEdit = (c) => { setForm(c); setEditing(c.id); setOpen(true); };

  const save = async () => {
    if (!form.nome.trim()) return toast.error("Il nome è obbligatorio");
    try {
      const payload = { nome: form.nome, email: form.email, telefono: form.telefono, indirizzo: form.indirizzo, note: form.note };
      if (editing) await api.put(`/clienti/${editing}`, payload);
      else await api.post("/clienti", payload);
      toast.success(editing ? "Cliente aggiornato" : "Cliente creato");
      setOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const remove = async (id) => {
    await api.delete(`/clienti/${id}`);
    toast.success("Cliente eliminato");
    load();
  };

  const filtered = clienti.filter((c) => {
    const q = query.toLowerCase();
    return (
      c.nome.toLowerCase().includes(q) ||
      (c.email || "").toLowerCase().includes(q) ||
      (c.telefono || "").toLowerCase().includes(q)
    );
  });

  const columns = [
    { id: "nome", label: "Nome", width: 240, sortable: true, sortValue: (c) => c.nome || "",
      cell: (c) => <span className="font-medium text-slate-900">{c.nome}</span> },
    { id: "email", label: "Email", width: 240, sortable: true, sortValue: (c) => c.email || "",
      cell: (c) => <span className="text-slate-600">{c.email || "—"}</span> },
    { id: "telefono", label: "Telefono", width: 180, sortable: true, sortValue: (c) => c.telefono || "",
      cell: (c) => <span className="text-slate-600">{c.telefono || "—"}</span> },
    { id: "indirizzo", label: "Indirizzo", width: 260, sortable: true, sortValue: (c) => c.indirizzo || "",
      cell: (c) => <span className="text-slate-600">{c.indirizzo || "—"}</span> },
    { id: "azioni", label: "Azioni", width: 120, align: "right", sortable: false,
      cell: (c) => (
        <div className="flex items-center justify-end gap-0.5">
          <Button variant="ghost" size="icon" data-testid={`edit-cliente-${c.id}`} onClick={() => openEdit(c)} className="h-8 w-8 text-slate-500 hover:text-blue-700"><Pencil className="h-4 w-4" /></Button>
          <ConfirmDelete title="Eliminare cliente?" description="Il cliente verrà rimosso definitivamente." actionTestid={`confirm-delete-cliente-${c.id}`} onConfirm={() => remove(c.id)}>
            <Button variant="ghost" size="icon" data-testid={`delete-cliente-${c.id}`} className="h-8 w-8 text-slate-500 hover:text-rose-600"><Trash2 className="h-4 w-4" /></Button>
          </ConfirmDelete>
        </div>
      ) },
  ];

  return (
    <>
      

      <div className="flex-1 overflow-auto px-6 py-6 lg:px-8 space-y-4">
        <PageHeader title="Clienti" subtitle="Anagrafica clienti collegata alle commesse">
        <Button data-testid="add-cliente-button" onClick={openNew} className="bg-blue-700 hover:bg-blue-800 active:scale-[0.98] transition-transform">
          <Plus className="h-4 w-4 mr-1.5" /> Nuovo cliente
        </Button>
      </PageHeader>
        <div className="relative max-w-sm">
          <Search className="h-4 w-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <Input data-testid="clienti-search" value={query}
            onChange={(e) => { setQuery(e.target.value); setPage(1); }}
            placeholder="Cerca per nome, email o telefono…" className="pl-9 bg-white" />
        </div>
        <Card className="rounded-md border-slate-200 shadow-sm overflow-hidden">
          {filtered.length === 0 ? (
            <Empty onAdd={openNew} hasQuery={!!query} />
          ) : (
            <DataTable
              tableKey="clienti" columns={columns} rows={filtered}
              rowKey={(c) => c.id} rowTestId={(c) => `cliente-row-${c.id}`}
              pageSize={PAGE_SIZE} containerClassName="max-h-[calc(100vh-300px)]"
              initialSort={{ key: "nome", dir: "asc" }}
            />
          )}
        </Card>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading">{editing ? "Modifica cliente" : "Nuovo cliente"}</DialogTitle>
            <DialogDescription>Anagrafica del cliente committente.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <Field label="Nome *" testid="cliente-nome-input" value={form.nome} onChange={(v) => setForm({ ...form, nome: v })} />
            <div className="grid grid-cols-2 gap-3">
              <Field label="Email" testid="cliente-email-input" value={form.email} onChange={(v) => setForm({ ...form, email: v })} />
              <Field label="Telefono" testid="cliente-telefono-input" value={form.telefono} onChange={(v) => setForm({ ...form, telefono: v })} />
            </div>
            <Field label="Indirizzo" testid="cliente-indirizzo-input" value={form.indirizzo} onChange={(v) => setForm({ ...form, indirizzo: v })} />
            <Field label="Note" testid="cliente-note-input" value={form.note} onChange={(v) => setForm({ ...form, note: v })} />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Annulla</Button>
            <Button data-testid="save-cliente-button" onClick={save} className="bg-blue-700 hover:bg-blue-800">Salva</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

const Field = ({ label, value, onChange, testid }) => (
  <div>
    <Label className="text-slate-700 text-sm">{label}</Label>
    <Input data-testid={testid} value={value || ""} onChange={(e) => onChange(e.target.value)} className="mt-1.5" />
  </div>
);

const Empty = ({ onAdd, hasQuery }) => (
  <div className="py-20 flex flex-col items-center text-center">
    <div className="h-14 w-14 rounded-lg bg-slate-100 flex items-center justify-center mb-4">
      <Users className="h-6 w-6 text-slate-400" />
    </div>
    <p className="text-slate-600 font-medium">{hasQuery ? "Nessun risultato" : "Nessun cliente"}</p>
    <p className="text-sm text-slate-400 mt-1 mb-4">
      {hasQuery ? "Prova un'altra ricerca." : "Aggiungi il primo cliente per iniziare."}
    </p>
    {!hasQuery && <Button onClick={onAdd} variant="outline"><Plus className="h-4 w-4 mr-1.5" /> Nuovo cliente</Button>}
  </div>
);
